import React, { useState, useEffect } from 'react';
import {
  SimpleForm,
  FormDataConsumer,
  SelectInput,
  TextInput,
  required,
  useTranslate,
} from 'react-admin';
import { useForm } from 'react-final-form';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Button,
  Tooltip,
  Accordion,
  AccordionDetails,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import DeleteIcon from '@material-ui/icons/Delete';
import EditIcon from '@material-ui/icons/Edit';
import AddIcon from '@material-ui/icons/Add';
import { v4 as uuidv4 } from 'uuid';
import { TextField, AccordionSummary, BottomToolbar, H3 } from '~/components';
import { LedgerMappingItem, Product } from '~/types/products';
import { useGetLedgerSystems } from '~/hooks/product';

export const l = (key: string): string => `resources.solutions.products.ledgerMappings.${key}`;

interface LedgerMappingProps {
  formData: Product;
}

const LedgerMapping = (props: LedgerMappingProps) => {
  const { formData } = props;
  const translate = useTranslate();
  const t = (key: string): string => translate(l(key));
  const classes = useStyles();
  const form = useForm();
  useEffect(() => {
    form.destroyOnUnregister = true;
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const { data: systems, isLoading: systemsLoading } = useGetLedgerSystems();

  const [open, setOpen] = useState(false);
  const [editIndex, setEditIndex] = useState<number>(-1);
  const [expanded, setExpanded] = useState<{ [x: string]: boolean }>({});

  const mappings: LedgerMappingItem[] = formData?.ledgerMapping || [];

  // Only allow one mapping per system
  const availableSystems = systems?.filter(
    (sys) => !mappings.some((m, idx) => m.system === sys && idx !== editIndex)
  );

  const handleChange = (key: string, isExpanded: boolean) => () => {
    setExpanded((prevProps) => ({ ...prevProps, [key]: !isExpanded }));
  };

  const handleOpen = () => setOpen(true);
  const handleClose = () => {
    setEditIndex(-1);
    setOpen(false);
  };

  const handleEdit = (index: number) => {
    setEditIndex(index);
    handleOpen();
  };

  const handleDelete = (index: number) => {
    form.change(
      'ledgerMapping',
      mappings.filter((_item, i) => i !== index)
    );
  };

  const handleAdd = () => {
    setEditIndex(-1);
    handleOpen();
  };

  const handleSubmit = (newRecord: any): void => {
    const newMapping = newRecord.ledgerMapping;
    let newMappings;
    if (editIndex > -1) {
      newMappings = mappings.map((item, idx) => (idx === editIndex ? newMapping : item));
    } else {
      newMappings = [...mappings, newMapping];
    }
    form.change('ledgerMapping', newMappings);
    handleClose();
  };

  return (
    <>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <H3>{t('ledgerMappings')}</H3>
        </Grid>
        {mappings?.map((item, index) => {
          const isExpanded = Boolean(expanded[`panel-${index}`]);
          return (
            <Grid key={index} item xs={12}>
              <Accordion
                expanded={isExpanded}
                onChange={handleChange(`panel-${index}`, isExpanded)}
              >
                <AccordionSummary
                  aria-controls={`${index}-content`}
                  id={`${index}-header`}
                  expanded={isExpanded}
                  heading={<TextField label={t('system')}>{item.system}</TextField>}
                  actionNode={
                    <>
                      <IconButton
                        type='button'
                        size='small'
                        onClick={() => handleEdit(index)}
                        className={classes.actionButton}
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        type='button'
                        size='small'
                        onClick={() => handleDelete(index)}
                        className={classes.actionButton}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </>
                  }
                />
                <AccordionDetails key='ledger-mapping-details' className={classes.AccordionDetails}>
                  <LedgerMappingView item={item} />
                </AccordionDetails>
              </Accordion>
            </Grid>
          );
        })}
        <Grid item xs={12}>
          <Tooltip title={availableSystems?.length === 0 ? t('allSystemsMapped') : ''}>
            <span>
              <Button
                onClick={handleAdd}
                disabled={availableSystems?.length === 0}
                color='primary'
                startIcon={<AddIcon />}
              >
                {t('addMapping')}
              </Button>
            </span>
          </Tooltip>
        </Grid>
      </Grid>
      <Dialog onClose={handleClose} aria-labelledby='ledger-mapping-dialog-title' open={open}>
        <DialogTitle id='ledger-mapping-dialog-title'>
          {editIndex > -1 ? t('editLedgerMapping') : t('addLedgerMapping')}
        </DialogTitle>
        <DialogContent className={classes.dialogContent}>
          <SimpleForm
            record={
              editIndex > -1
                ? { ledgerMapping: mappings[editIndex] }
                : { ledgerMapping: { id: uuidv4() } }
            }
            toolbar={<BottomToolbar onSave={handleSubmit} onClose={handleClose} />}
            margin='none'
            className={classes.form}
            redirect={false}
          >
            <FormDataConsumer>
              {({ formData }: any) => (
                <LedgerMappingFormInput
                  formData={formData.ledgerMapping}
                  systems={availableSystems}
                  systemsLoading={systemsLoading}
                  edit={editIndex > -1}
                />
              )}
            </FormDataConsumer>
          </SimpleForm>
        </DialogContent>
      </Dialog>
    </>
  );
};

interface LedgerMappingFormInputProps {
  formData: LedgerMappingItem;
  systems?: string[];
  systemsLoading?: boolean;
  edit?: boolean;
}

const LedgerMappingFormInput = ({
  formData,
  systems,
  systemsLoading,
  edit,
}: LedgerMappingFormInputProps) => {
  const translate = useTranslate();
  const t = (key: string): string => translate(l(key));
  const system = formData?.system;
  const form = useForm();
  return (
    <>
      <TextInput source='ledgerMapping.id' label={t('id')} disabled fullWidth />
      <SelectInput
        source='ledgerMapping.system'
        label={t('system')}
        choices={systems?.map((system) => ({
          id: system,
          name: system,
        }))}
        disabled={edit || systemsLoading}
        validate={required()}
        fullWidth
      />
      {system === 'MAMBU' && (
        <>
          <SelectInput
            source='ledgerMapping.type'
            label={t('type')}
            choices={[
              { id: 'DEPOSIT', name: t('DEPOSIT') },
              { id: 'POOL_DEPOSIT', name: t('POOL_DEPOSIT') },
            ]}
            validate={required()}
            fullWidth
            onChange={(event: any) => {
              const newType = event.target.value;
              if (newType === 'POOL_DEPOSIT') {
                form.change('ledgerMapping.encodedKey', undefined); // Clear encodedKey
              }
            }}
          />
          {formData?.type === 'POOL_DEPOSIT' && (
            <TextInput
              source='ledgerMapping.encodedKey'
              label={t('encodedKey')}
              validate={required()}
              fullWidth
            />
          )}
        </>
      )}
    </>
  );
};

export const LedgerMappingView = ({ item }: { item: LedgerMappingItem }) => {
  const translate = useTranslate();
  const t = (key: string): string => translate(l(key));
  return (
    <Grid container direction='column' spacing={1}>
      <Grid item>
        <TextField label={t('id')}>{item?.id}</TextField>
      </Grid>
      {item?.system === 'MAMBU' && (
        <>
          <Grid item>
            <TextField label={t('type')}>{item?.type}</TextField>
          </Grid>
          {item?.type === 'POOL_DEPOSIT' && (
            <Grid item>
              <TextField label={t('encodedKey')}>{item?.encodedKey}</TextField>
            </Grid>
          )}
        </>
      )}
    </Grid>
  );
};

const useStyles = makeStyles((theme) => ({
  AccordionDetails: {
    paddingTop: theme.spacing(0),
    paddingBottom: theme.spacing(3),
    display: 'block',
  },
  form: {
    '& > div': {
      padding: 0,
      '&:first-child': {
        paddingTop: 0,
      },
    },
  },
  dialogContent: {
    paddingTop: 0,
    paddingBottom: theme.spacing(2),
    paddingLeft: theme.spacing(3),
    paddingRight: theme.spacing(3),
    width: 360,
    boxSizing: 'border-box',
  },
  actionButton: {
    color: theme.palette.text.primary,
  },
}));

export default LedgerMapping;
